"use client";

import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { MixerHorizontalIcon } from "@radix-ui/react-icons";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";

export function DataTableViewOptions({ table, viewModeGroup = null }) {
  if (!table) {
    return null
  }

  // 获取所有叶子列（用于检查可见性）
  const allLeafColumns = table.getAllLeafColumns().filter(
    (column) =>
      (typeof column.accessorFn !== "undefined" || typeof column.accessorKey !== "undefined") && column.getCanHide()
  )

  // 获取 cost 和 revenue 相关的叶子列
  const costColumns = allLeafColumns.filter((column) => column.id.startsWith('costs-'))
  const revenueColumns = allLeafColumns.filter((column) => column.id.startsWith('revenues-'))
  
  // 通过分析叶子列的 ID 模式来识别嵌套组列（例如 Total-revenue, Total-costs 等）
  // 找出所有以特定模式开头的列，提取父列 ID
  const nestedGroupMap = new Map()
  allLeafColumns.forEach((column) => {
    // 匹配模式：${parentId}-revenue, ${parentId}-costs, ${parentId}-profit 等
    // 但不包括 costs- 和 revenues- 开头的列
    if (!column.id.startsWith('costs-') && !column.id.startsWith('revenues-')) {
      // 匹配模式：code-revenue, code-costs, code-profit, code-er, code-roas, code-cpi
      const match = column.id.match(/^([^-]+(?:[_-][^-]+)*?)-(revenue|costs|profit|er|roas|cpi|installs)$/)
      if (match) {
        const parentId = match[1]
        if (!nestedGroupMap.has(parentId)) {
          nestedGroupMap.set(parentId, [])
        }
        nestedGroupMap.get(parentId).push(column)
      }
    }
  })
  
  // 转换为数组格式，每个元素包含父列 ID 和子列
  const nestedGroupColumns = Array.from(nestedGroupMap.entries()).map(([parentId, children]) => ({
    id: parentId,
    children
  }))

  // 其他叶子列（不包括 cost、revenue 和嵌套组列的子列）
  const nestedGroupChildIds = new Set()
  nestedGroupColumns.forEach((group) => {
    group.children.forEach((child) => nestedGroupChildIds.add(child.id))
  })
  
  const otherColumns = allLeafColumns.filter(
    (column) => {
      const isCost = column.id.startsWith('costs-')
      const isRevenue = column.id.startsWith('revenues-')
      const isNestedChild = nestedGroupChildIds.has(column.id)
      return !isCost && !isRevenue && !isNestedChild
    }
  )

  // 检查 cost 列是否全部可见
  const allCostVisible = costColumns.length > 0 && costColumns.every((col) => col.getIsVisible())
  // 检查 revenue 列是否全部可见
  const allRevenueVisible = revenueColumns.length > 0 && revenueColumns.every((col) => col.getIsVisible())

  // 一键切换 cost 列
  const toggleCostColumns = (value) => {
    costColumns.forEach((column) => {
      column.toggleVisibility(!!value)
    })
  }

  // Bulk toggles for per-source metrics under Costs (e.g. all Install / CPI)
  const costMetricGroups = ['install', 'cpi']
    .map((metric) => ({
      metric,
      columns: costColumns.filter((col) => col.id.endsWith(`-${metric}`)),
    }))
    .filter((group) => group.columns.length > 0)

  // 一键切换 revenue 列
  const toggleRevenueColumns = (value) => {
    revenueColumns.forEach((column) => {
      column.toggleVisibility(!!value)
    })
  }

  // 检查嵌套组列的所有子列是否可见
  const isNestedGroupAllVisible = (group) => {
    return group.children.length > 0 && group.children.every((col) => col.getIsVisible())
  }

  // 切换嵌套组列的所有子列
  const toggleNestedGroupColumns = (group, value) => {
    group.children.forEach((column) => {
      column.toggleVisibility(!!value)
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto hidden h-8 lg:flex"
        >
          <MixerHorizontalIcon className="mr-2 h-4 w-4" />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto max-h-[80vh] overflow-y-auto">
        {viewModeGroup && (
          <>
            <DropdownMenuRadioGroup
              value={viewModeGroup.value}
              onValueChange={viewModeGroup.onValueChange}
            >
              {viewModeGroup.options.map((option) => (
                <DropdownMenuRadioItem key={option.id} value={option.id}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Cost 子菜单 */}
        {costColumns.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="capitalize">Costs</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
              <DropdownMenuCheckboxItem
                checked={allCostVisible}
                onCheckedChange={toggleCostColumns}
                className="font-semibold"
              >
                全部
              </DropdownMenuCheckboxItem>
              {costMetricGroups.map(({ metric, columns }) => (
                <DropdownMenuCheckboxItem
                  key={metric}
                  checked={columns.every((col) => col.getIsVisible())}
                  onCheckedChange={(value) =>
                    columns.forEach((col) => col.toggleVisibility(!!value))
                  }
                  className="font-semibold"
                >
                  {metric === 'cpi' ? 'CPI' : 'Install'}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              {costColumns.map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize pl-6"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {column.id.replace(/^costs-/, '')}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {/* Revenue 子菜单 */}
        {revenueColumns.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="capitalize">Revenue</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
              <DropdownMenuCheckboxItem
                checked={allRevenueVisible}
                onCheckedChange={toggleRevenueColumns}
                className="font-semibold"
              >
                全部
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {revenueColumns.map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize pl-6"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {column.id.replace(/^revenues-/, '')}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {/* 嵌套组列（如 Total, Total1 等） */}
        {nestedGroupColumns.map((group) => {
          const allVisible = isNestedGroupAllVisible(group)
          return (
            <DropdownMenuSub key={group.id}>
              <DropdownMenuSubTrigger className="capitalize">{group.id}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
                <DropdownMenuCheckboxItem
                  checked={allVisible}
                  onCheckedChange={(value) => toggleNestedGroupColumns(group, value)}
                  className="font-semibold"
                >
                  全部
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                {group.children.map((column) => {
                  // 提取列名（去掉父列 ID 前缀）
                  const displayName = column.id.replace(`${group.id}-`, '')
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize pl-6"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {displayName}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )
        })}
        {/* 其他列选项 */}
        {otherColumns.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {otherColumns.map((column) => {
              return (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {column.id}
                </DropdownMenuCheckboxItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
